import React from "react"
import { Box, FormLabel, Typography, dayjs } from "@carrotfertility/carotene-core"
import {
  FormCheckbox,
  FormCheckboxGroup,
  FormDatePicker,
  FormRadioGroup,
  FormSelect,
  FormTextField
} from "@carrotfertility/carotene-core-x"
import { Markdown } from "@internal/ui"

// A Healthie "conditional form question" rule: this question is shown only when the controlling
// question's answer satisfies `filter_type`/`value_to_filter`. Null/absent means always shown; the
// operators are classified in conditions.ts.
export interface CustomModuleCondition {
  id: string
  // The CONTROLLING question whose answer gates display of this one.
  conditional_custom_module_id?: string | null
  // The DEPENDENT question this condition belongs to (this question's own id).
  custom_module_id?: string | null
  // Operator token ("filter_type"): equals / contains / numeric comparison / …
  filter_type?: string | null
  // Value the controlling answer is compared against.
  value_to_filter?: string | null
}

export interface CustomModule {
  id: string
  label: string | null
  sublabel: string | null
  hipaa_name: string | null
  mod_type: string
  options: string | null
  options_array: string[] | null
  required: boolean | null
  position: number | null
  parent_custom_module_id: string | null
  custom_module_condition?: CustomModuleCondition | null
  controls_conditional_modules?: boolean | null
}

// Prefer Healthie's structured `options_array`; fall back to newline-delimited `options`.
export function parseOptions(mod: CustomModule): Array<{ label: string; value: string }> {
  const raw = mod.options_array ?? (mod.options ?? "").split(/\r?\n/)
  return raw
    .map((s) => s.trim())
    .filter(Boolean)
    .map((o) => ({ label: o, value: o }))
}

export const DISPLAY_ONLY_TYPES = new Set(["read_only", "label"])
const DATE_TYPES = new Set(["date", "date_picker", "dob"])
// Healthie returns these questions' `mod_type` as the literal label string (not a token like other
// types), so detection keys on them exactly. If the clinical team relabels a field, update these.
const HEIGHT_MOD_TYPE = "Height (in.)"
const WEIGHT_MOD_TYPE = "Weight"

// Allow-list of every mod_type this renderer explicitly handles (each has a switch case below).
// An allow-list, not a deny-list: any type NOT here renders nothing and is reported once per form
// (see IntakeForm), so a new/unknown Healthie type surfaces instead of rendering as a guessed control.
export const SUPPORTED_TYPES = new Set<string>([
  ...DISPLAY_ONLY_TYPES,
  ...DATE_TYPES,
  "textarea",
  "email",
  "phone",
  "number",
  "dropdown",
  "radio",
  "horizontal_radio",
  "checkbox",
  "agree_to_above",
  HEIGHT_MOD_TYPE,
  WEIGHT_MOD_TYPE,
  "text",
  "name"
])

export const isMultiSelect = (mod: CustomModule): boolean => mod.mod_type === "checkbox"
export const isBoolean = (mod: CustomModule): boolean => mod.mod_type === "agree_to_above"
export const isDate = (mod: CustomModule): boolean => DATE_TYPES.has(mod.mod_type)
// A composite "height" question: feet + inches inputs, submitted as total inches.
// Exported: conditions.ts normalizes a height controller's value before evaluating a condition.
export const isHeight = (mod: CustomModule): boolean => mod.mod_type === HEIGHT_MOD_TYPE
// A "weight" question: a single number, submitted as-is; labeled with its unit (lbs).
const isWeight = (mod: CustomModule): boolean => mod.mod_type === WEIGHT_MOD_TYPE
export const isInput = (mod: CustomModule): boolean =>
  SUPPORTED_TYPES.has(mod.mod_type) && !DISPLAY_ONLY_TYPES.has(mod.mod_type)

// Prefixed so a numeric Healthie id isn't read as an array index in the RHF field path.
export const fieldName = (mod: CustomModule): string => `q_${mod.id}`

// A height answer holds feet + inches in one field; it submits as total inches (see the height case).
export interface HeightValue {
  feet: string
  inches: string
}

// Date controls hold a dayjs object, not a string; height holds a { feet, inches } pair.
export type FieldValue = string | string[] | boolean | dayjs.Dayjs | HeightValue | null

export function toDefaultValue(mod: CustomModule, answer: string): FieldValue {
  if (isMultiSelect(mod)) return answer.split(/\r?\n/).filter(Boolean)
  if (isBoolean(mod)) return answer === "true" || answer === "1"
  if (isDate(mod)) return answer ? dayjs(answer) : null
  if (isHeight(mod)) {
    // Split a stored total-inches answer back into feet + inches (empty when there's no answer yet).
    const total = parseInt(answer, 10)
    if (!answer || Number.isNaN(total)) return { feet: "", inches: "" }
    return { feet: String(Math.floor(total / 12)), inches: String(total % 12) }
  }
  return answer
}

export function toHealthieAnswer(mod: CustomModule, value: unknown): string {
  if (isMultiSelect(mod)) return Array.isArray(value) ? value.join("\n") : ""
  if (isBoolean(mod)) return value === true ? "true" : ""
  if (isDate(mod)) return dayjs.isDayjs(value) && value.isValid() ? value.format("YYYY-MM-DD") : ""
  if (isHeight(mod)) {
    const v = (value ?? {}) as Partial<HeightValue>
    const feet = typeof v.feet === "string" ? v.feet.trim() : ""
    const inches = typeof v.inches === "string" ? v.inches.trim() : ""
    if (feet === "" && inches === "") return ""
    // Submit as total inches (feet * 12 + inches), matching the Carrot Plans height step.
    return String((parseInt(feet, 10) || 0) * 12 + (parseInt(inches, 10) || 0))
  }
  return typeof value === "string" ? value : ""
}

// Healthie's rich-text editor emits spacer paragraphs (`<p>&nbsp;</p>`) that stack on top
// of the shared markdown <p> spacing into oversized gaps. Strip them so the renderer alone
// controls spacing — a local workaround rather than forking @internal/ui's base elements
// (shared by ~15 host consumers) with a telemedicine-specific spacing scale. No-op for
// non-HTML copy (no <p> tags to match).
const EMPTY_PARAGRAPH_RE = /<p\b[^>]*>(?:\s|&nbsp;|&#160;|&#xa0;|<span\b[^>]*>|<\/span>|<br\s*\/?>)*<\/p>/gi
function stripSpacerParagraphs(html: string): string {
  return html.replace(EMPTY_PARAGRAPH_RE, "")
}

// Renders one Healthie question as the matching carotene-core-x control, keyed off `mod_type`.
export function FormField({ mod, name: nameOverride }: { mod: CustomModule; name?: string }): JSX.Element | null {
  const baseLabel = mod.label || mod.hipaa_name || "(untitled)"
  // Weight is captured in pounds; make the unit explicit in the label.
  const label = isWeight(mod) ? `${baseLabel} (lbs.)` : baseLabel
  const name = nameOverride ?? fieldName(mod)
  const required = mod.required ?? false
  const requiredRule = required ? "Required" : false
  const helperText = mod.sublabel ?? undefined
  const options = parseOptions(mod)

  const styledLabel = <FormLabel sx={(theme) => ({ ...theme.typography.body1 })}>{label}</FormLabel>

  const groupLabel = (
    <FormLabel required={required} sx={(theme) => ({ ...theme.typography.body1 })}>
      {label}
    </FormLabel>
  )

  // Composite sub-inputs stay required (validation + aria-required) but hide their own asterisk — the
  // one on the group label above already marks the question required, so repeating it reads as clutter.
  const hideAsteriskSx = { "& .MuiFormLabel-asterisk": { display: "none" } }

  const labeledField = (control: JSX.Element): JSX.Element => (
    <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.tokens.spacing.sm })}>
      <FormLabel htmlFor={name} required={required} sx={(theme) => ({ ...theme.typography.body1 })}>
        {label}
      </FormLabel>
      {control}
    </Box>
  )

  const textField = (type: string, extra?: Record<string, unknown>): JSX.Element =>
    labeledField(
      <FormTextField
        name={name}
        id={name}
        helperText={helperText}
        required={required}
        registerOptions={{ required: requiredRule }}
        fullWidth
        type={type}
        {...extra}
      />
    )
  const select = (): JSX.Element =>
    labeledField(
      <FormSelect
        name={name}
        id={name}
        helperText={helperText}
        required={required}
        controllerProps={{ rules: { required: requiredRule } }}
        fullWidth
        options={options}
      />
    )

  const displayText = (mod.label ?? "").trim()
  // read_only copy lives in `options` as rich-text HTML (label is a placeholder); fall back to label.
  const readOnlyContent = stripSpacerParagraphs(mod.options ?? "").trim() || displayText

  // Any type without an explicit case renders nothing; it's reported once at form load in IntakeForm
  // (not here — this runs on every re-render), so a form we can't fully render is flagged.
  if (!SUPPORTED_TYPES.has(mod.mod_type)) return null

  switch (mod.mod_type) {
    case "read_only":
      return readOnlyContent ? <Markdown>{readOnlyContent}</Markdown> : null
    case "label":
      return displayText ? (
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: (theme) => theme.typography.fontWeightBold }}>
          <Markdown inline>{displayText}</Markdown>
        </Typography>
      ) : null

    case "textarea":
      return labeledField(
        <FormTextField
          name={name}
          id={name}
          helperText={helperText}
          required={required}
          registerOptions={{ required: requiredRule }}
          fullWidth
          multiline
          minRows={3}
        />
      )
    case "date":
    case "date_picker":
    case "dob":
      return (
        <FormDatePicker
          name={name}
          label={styledLabel}
          helperText={helperText}
          required={required}
          controllerProps={{ rules: { required: requiredRule } }}
        />
      )
    case "email":
      return textField("email")
    case "phone":
      return textField("tel")
    case "number":
      return textField("number")

    case "dropdown":
      return select()
    case "radio":
    case "horizontal_radio":
      return (
        <FormRadioGroup
          name={name}
          label={styledLabel}
          helperText={helperText}
          required={required}
          controllerProps={{ rules: { required: requiredRule } }}
          options={options}
        />
      )
    case "checkbox":
      return (
        <FormCheckboxGroup
          name={name}
          label={styledLabel}
          helperText={helperText}
          required={required}
          controllerProps={{ rules: { required: requiredRule } }}
          options={options}
        />
      )
    case "agree_to_above":
      return (
        <FormCheckbox
          name={name}
          label={
            <>
              <Markdown inline>{label}</Markdown>
              {required ? " *" : ""}
            </>
          }
          controllerProps={{ defaultValue: false, rules: { required: requiredRule } }}
        />
      )

    case HEIGHT_MOD_TYPE:
      // Feet + inches inputs collected as one answer, submitted as total inches (see toHealthieAnswer).
      // `.feet`/`.inches` nest under this module's field so the value is one object. Feet carries the
      // required rule (a height needs feet; inches defaults to 0).
      return (
        <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.tokens.spacing.xs })}>
          {groupLabel}
          <Box
            sx={(theme) => ({
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: theme.tokens.spacing.sm
            })}
          >
            {/* required stays for validation; the asterisk is hidden here (shown on the group label). */}
            <FormTextField
              name={`${name}.feet`}
              label="Feet"
              type="number"
              required={required}
              registerOptions={{ required: requiredRule }}
              inputProps={{ min: 0 }}
              sx={hideAsteriskSx}
              fullWidth
            />
            <FormTextField
              name={`${name}.inches`}
              label="Inches"
              type="number"
              inputProps={{ min: 0, max: 11 }}
              fullWidth
            />
          </Box>
        </Box>
      )

    case WEIGHT_MOD_TYPE:
      return textField("number", { inputProps: { min: 0 } })

    case "text":
    case "name":
      if (options.length > 0) return select()
      return textField("text")
    default:
      // Unreachable: unsupported types are dropped by the guard above. Kept for switch exhaustiveness.
      return null
  }
}
